// Pure helpers for the AU cadastre WMS overlay rendered on top of the
// Google base map. Extracted out of MapView.js to keep that component
// focused on React state / orchestration. No React, no side effects —
// safe to unit-test in isolation.
//
// The single dependency is `pickProviderForLatLng` from BoundariesControl,
// which is itself a pure function over a static provider table.

import { pickProviderForLatLng } from './BoundariesControl';

// Convert a Google Maps tile (x, y, z) coordinate to its web-mercator BBOX
// string + lng/lat centroid. The centroid is used to pick the right state
// cadastre provider; the BBOX is what we hand to the WMS server.
//
// Web Mercator (EPSG:3857):
//   X = R * lon_rad,
//   Y = R * ln(tan(π/4 + lat_rad / 2)),
// where R = 6378137 m. Y must be scaled by R directly — not by the
// longitude scale (R * π / 180). That was the previous off-by-57x bug.
function tileToBBox(coord, zoom) {
  const n = Math.pow(2, zoom);
  const lonMin = coord.x / n * 360 - 180;
  const lonMax = (coord.x + 1) / n * 360 - 180;
  const latRad1 = Math.atan(Math.sinh(Math.PI * (1 - 2 * (coord.y + 1) / n)));
  const latRad2 = Math.atan(Math.sinh(Math.PI * (1 - 2 * coord.y / n)));
  const latMin = latRad1 * 180 / Math.PI;
  const latMax = latRad2 * 180 / Math.PI;
  const R = 6378137;
  const toMercX = (lon) => lon * Math.PI / 180 * R;
  const toMercY = (lat) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)) * R;
  return {
    merc: `${toMercX(lonMin)},${toMercY(latMin)},${toMercX(lonMax)},${toMercY(latMax)}`,
    centroid: { lng: (lonMin + lonMax) / 2, lat: (latMin + latMax) / 2 },
  };
}

// Build a Google Maps custom MapType that fetches each WMS tile, runs it
// through a canvas, drops semi-transparent pixels (= polygon fills like
// long creek/easement parcels), and recolours the remaining strong line
// strokes to the user's chosen colour.
//
// The boundary WMS endpoints return both stroked lines (alpha ≈ 255) and
// lighter fills (alpha ≈ 30–120). Filtering on an alpha threshold gives
// us "lines only".
//
// Some providers (WA / NT / ACT) return tiles without CORS headers — for
// those we skip canvas processing entirely and inline a plain <img>
// recoloured by the same CSS filter as the tint overlay.
export function buildCanvasTileMapType(colourCssHex, colourFilter, lineOpacity) {
  // Parse target colour once.
  const [tr, tg, tb] = [
    parseInt(colourCssHex.slice(1, 3), 16),
    parseInt(colourCssHex.slice(3, 5), 16),
    parseInt(colourCssHex.slice(5, 7), 16),
  ];
  return {
    tileSize: new window.google.maps.Size(256, 256),
    maxZoom: 22,
    name: 'AU Cadastre',
    alt: 'AU Cadastre',
    getTile(coord, zoom, ownerDocument) {
      const div = ownerDocument.createElement('div');
      div.style.width = '256px';
      div.style.height = '256px';
      div.style.pointerEvents = 'none';
      // Custom MapType objects (anything with getTile/releaseTile) ignore
      // the `opacity` property — that's only honoured by ImageMapType. We
      // tag every tile div with a marker class and apply the line opacity
      // via inline style here, so the build-time value is correct. A
      // separate sync effect in MapView pushes live slider changes onto
      // all existing tile divs without rebuilding the whole overlay.
      div.classList.add('rm-cadastre-tile');
      div.style.opacity = String(typeof lineOpacity === 'number' ? lineOpacity : 1);

      const { merc, centroid } = tileToBBox(coord, zoom);
      const provider = pickProviderForLatLng(centroid.lng, centroid.lat);
      if (!provider) return div;

      const url = provider.buildTileUrl(merc);

      // Raw-only providers (WA / NT / ACT): their WMS endpoints don't
      // send CORS headers, so `getImageData` would throw a SecurityError.
      // These services return transparent line tiles (no polygon fills),
      // so we skip canvas processing entirely and inline the tile as a
      // plain <img> recoloured by the same CSS filter the tint overlay
      // uses.
      if (provider.rawOnly) {
        const rawImg = ownerDocument.createElement('img');
        rawImg.src = url;
        rawImg.width = 256;
        rawImg.height = 256;
        rawImg.style.width = '256px';
        rawImg.style.height = '256px';
        rawImg.style.display = 'block';
        rawImg.style.pointerEvents = 'none';
        if (colourFilter) rawImg.style.filter = colourFilter;
        rawImg.onerror = () => { /* tile may 404 outside the state — drop quietly */ };
        div.appendChild(rawImg);
        return div;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = ownerDocument.createElement('canvas');
          canvas.width = 256; canvas.height = 256;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, 256, 256);
          const src = ctx.getImageData(0, 0, 256, 256);
          const sdata = src.data;
          const out = ctx.createImageData(256, 256);
          const odata = out.data;
          const OPAQUE = 80; // anything stronger than this counts as "drawn"
          const W = 256, H = 256;
          // Edge detection: keep pixel iff opaque AND at least one of its
          // 8 neighbours is transparent. This converts polygon fills into
          // 1px outlines while preserving existing line strokes (which
          // are already at the edge of their thin geometry).
          const idx = (x, y) => (y * W + x) * 4 + 3;
          for (let y = 0; y < H; y++) {
            for (let x = 0; x < W; x++) {
              const i = (y * W + x) * 4;
              if (sdata[i + 3] < OPAQUE) continue; // transparent — skip
              let allOpaqueNeighbours = true;
              // Skip border row/col — keep them so map tiles seam together.
              if (x === 0 || y === 0 || x === W - 1 || y === H - 1) {
                allOpaqueNeighbours = false;
              } else {
                for (let dy = -1; dy <= 1 && allOpaqueNeighbours; dy++) {
                  for (let dx = -1; dx <= 1; dx++) {
                    if (dx === 0 && dy === 0) continue;
                    if (sdata[idx(x + dx, y + dy)] < OPAQUE) {
                      allOpaqueNeighbours = false;
                      break;
                    }
                  }
                }
              }
              if (!allOpaqueNeighbours) {
                // Edge / line stroke — paint with the user's colour.
                odata[i] = tr; odata[i + 1] = tg; odata[i + 2] = tb; odata[i + 3] = 255;
              }
              // else: interior of a polygon fill — leave fully transparent.
            }
          }
          ctx.putImageData(out, 0, 0);
          canvas.style.width = '256px';
          canvas.style.height = '256px';
          div.appendChild(canvas);
        } catch (e) {
          // CORS or other failure — silently fail.
        }
      };
      img.onerror = () => { /* ignore failed tiles */ };
      img.src = url;
      return div;
    },
    releaseTile() { /* nothing to clean up */ },
  };
}
